import React from 'react';
import loadable from '@loadable/component';
import Spinner from 'src/common/refactoredComponents/spinner/Spinner';// import Home from 'src/pages/home';
// import Maintainance from 'src/common/components/maintainance';
// import Consignment from 'src/pages/consignmentBilling/index';
// import ConsignmentReturn from 'src/pages/consignmentReturn/index';
import ConsignmentInventory from 'src/pages/consignmentBilling/components/inventory';
import ConsignmentReturnInventory from 'src/pages/consignmentReturn/components/inventory';
import { sizes } from 'src/styles/Media.styled';
import {
    CC_USER_ROLE,
    ITA_USER_ROLE,
    FIM_USER_ROLE,
    CSR_USER_ROLE,
    FIA_USER_ROLE,
} from 'src/constants/appConstants';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { checkImpersonationActive, checkProxyActive } from 'src/utils/util';

const LoadingComponent = (
    <Spinner
        id="app-routes-spinner"
        background="#f5f5f5"
        animation="border"
        variant="primary"
        small={window.innerWidth <= sizes.mobileDevice}
        height={window.innerWidth <= sizes.mobileDevice ? '200px' : '55%'}
    ></Spinner>
);

const Maintainance = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/common/components/maintainance' /* webpackChunkName: "Maintainance" */
        ),
    {
        fallback: LoadingComponent,
    }
);

const Home = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/home' /* webpackChunkName: "Calendar" */
        ),
    {
        fallback: LoadingComponent,
    }
);

const ScheduleSurgery = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/scheduleSurgery' /* webpackChunkName: "ScheduleSurgery" */
        ),
    {
        fallback: LoadingComponent,
    }
);

// Forces a fresh mount of the surgery flow on every navigation into /scheduleSurgery
// (location.key changes only on a push/replace). This makes "Copy" from the order
// submission/success screen — which navigates to /scheduleSurgery while already on
// it — behave exactly like copying from the order listing page: a full remount that
// re-runs the copy-populate flow instead of leaving stale form state behind.
const ScheduleSurgeryRemountable = (props) => {
    const location = useLocation();
    return <ScheduleSurgery key={location.key} {...props} />;
};

const Equity = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/equity' /* webpackChunkName: "Equity" */
        ),
    {
        fallback: LoadingComponent,
    }
);

// Copying an equity order from the submission/success screen navigates from
// /modifyEquity/<oldRef> to /modifyEquity/<newRef> (same route pattern). With
// route key={index}, React reuses the component instance, so the mount-only load
// and the local viewSurgerySuccess state never reset — the summary just closes
// and the success screen stays. Keying the component by location.key forces a
// genuine remount on every push/replace (like a cross-route copy), so the copied
// order populates correctly. Native history.replaceState (URL cleanup) does not
// change location.key, so in-flow URL rewrites do not cause spurious remounts.
const EquityRemountable = (props) => {
    const location = useLocation();
    return <Equity key={location.key} {...props} />;
};

const ConsignmentReturn = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/consignmentReturn' /* webpackChunkName: "ConsignmentReturn" */
        ),
    {
        fallback: LoadingComponent,
    }
);

const Consignment = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/consignmentBilling' /* webpackChunkName: "ConsignmentBilling" */
        ),
    {
        fallback: LoadingComponent,
    }
);

const Orders = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/orders' /* webpackChunkName: "Orders" */
        ),
    { fallback: LoadingComponent }
);

const Admin = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/admin' /* webpackChunkName: "Admin" */
        ),
    { fallback: LoadingComponent }
);
const ReceiveSets = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/receiveSets' /* webpackChunkName: "ReceiveSets" */
        ),
    {
        fallback: LoadingComponent,
    }
);
const HomeScreen = () => {
    const mainMenuVisibility = useSelector(state => state.loginReducer.mainMenuVisibility);
    if (
        (![ 
            CC_USER_ROLE,
            ITA_USER_ROLE,
            FIM_USER_ROLE,
            FIA_USER_ROLE,
            CSR_USER_ROLE,
        ].includes(sessionStorage.getItem('employeeRole')) &&
            !checkImpersonationActive() &&
            !checkProxyActive()) ||
        ((checkImpersonationActive() || checkProxyActive()) &&
            ![ 
                CC_USER_ROLE,
                ITA_USER_ROLE,
                FIM_USER_ROLE,
                FIA_USER_ROLE,
                CSR_USER_ROLE,
            ].includes(sessionStorage.getItem('ProxyUserRole'))) && mainMenuVisibility?.home
    ) {
        return Home;
    } else if (sessionStorage.getItem('employeeRole') === CSR_USER_ROLE && mainMenuVisibility?.orders) {
        return Orders;
    } else if (
        (sessionStorage.getItem('employeeRole') === FIM_USER_ROLE ||
            sessionStorage.getItem('employeeRole') === FIA_USER_ROLE) &&
        window.location.pathname.length == 1 && mainMenuVisibility?.receiveSets
    ) {
        return ReceiveSets;
    } else {
        return mainMenuVisibility.admin ? Admin : Orders;
    }
};
const Announcements = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/more/pages/announcements' /* webpackChunkName: "Announcements" */
        ),
    {
        fallback: LoadingComponent,
    }
);
const Settings = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/more/pages/settings' /* webpackChunkName: "Settings" */
        ),
    {
        fallback: LoadingComponent,
    }
);
const Accounts = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/more/pages/accounts' /* webpackChunkName: "Accounts" */
        ),
    {
        fallback: LoadingComponent,
    }
);
export const Links = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/more/pages/links' /* webpackChunkName: "Links" */
        ),
    {
        fallback: LoadingComponent,
    }
);
const Physicians = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/more/pages/physicians' /* webpackChunkName: "Physicians" */
        ),
    {
        fallback: LoadingComponent,
    }
);
const Proxies = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/more/pages/proxies' /* webpackChunkName: "Proxies" */
        ),
    {
        fallback: LoadingComponent,
    }
);
const ProductGuide = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/more/pages/productGuide' /* webpackChunkName: "ProductGuide" */
        ),
    {
        fallback: LoadingComponent,
    }
);
const Templates = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/more/pages/templates' /* webpackChunkName: "Templates" */
        ),
    {
        fallback: LoadingComponent,
    }
);
const OrderHistory = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/more/pages/orderHistory' /* webpackChunkName: "OrderHistory" */
        ),
    {
        fallback: LoadingComponent,
    }
);
const BackOrders = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/backOrders' /* webpackChunkName: "BackOrders" */
        ),
    {
        fallback: LoadingComponent,
    }
);
export const Login = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/login' /* webpackChunkName: "Login" */
        ),
    {
        fallback: LoadingComponent,
    }
);

export const ImpersonationLandingScreen = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/impersonation' /* webpackChunkName: "Impersonation" */
        ),
    {
        fallback: LoadingComponent,
    }
);

export const ReportUsage = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/reportUsage' /* webpackChunkName: "ReportUsage" */
        ),
    {
        fallback: LoadingComponent,
    }
);

export const NUTRProcedure = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/procedure' /* webpackChunkName: "NUTRProcedure" */
        ),
    {
        fallback: LoadingComponent,
    }
);

export const ErrorComponent = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/common/components/errors/ForbiddenError' /* webpackChunkName: "Error" */
        ),
    {
        fallback: LoadingComponent,
    }
);
export const ConsignmentAdd = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/consignmentAdd' /* webpackChunkName: "ConsignmentAdd" */
        ),
    {
        fallback: LoadingComponent,
    }
);
export const FieldInventoryDashboard = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/more/pages/fieldInventoryDashboard' /* webpackChunkName: "FieldInventoryDashboard" */
        ),
    {
        fallback: LoadingComponent,
    }
);
export const Overrides = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/overrides' /* webpackChunkName: "Overrides" */
        ),
    {
        fallback: LoadingComponent,
    }
);

export const Alerts = loadable(
    () =>
        import(
            /* webpackPrefetch: true */ 'src/pages/alerts' /* webpackChunkName: "AlertsPage" */
        ),
    {
        fallback: LoadingComponent,
    }
);

export const routes = [
    { exact: true, path: '/', component: HomeScreen },
    { exact: true, path: '/orders', component: Orders },
    { exact: true, path: '/home', component: Home },
    { path: '/orders/:linkMyCaseRef', component: Orders },
    { path: '/scheduleSurgery', component: ScheduleSurgeryRemountable },
    { path: '/modifySurgery/:myCaseRef', component: ScheduleSurgeryRemountable },
    { path: '/transferSurgery/:myCaseRef', component: ScheduleSurgery },
    { path: '/settings', component: Settings },
    { path: '/announcements', component: Announcements },
    { path: '/reportUsage', component: ReportUsage },
    { path: '/nutrprocedure', component: NUTRProcedure },
    { path: '/maintainance', component: Maintainance },
    { path: '/accounts', component: Accounts },
    { path: '/links', component: Links },
    { path: '/equity', component: EquityRemountable },
    { path: '/modifyEquity/:myCaseRef', component: EquityRemountable },
    { path: '/consignmentBilling', component: Consignment },
    { path: '/consignmentReturn', component: ConsignmentReturn },
    { path: '/inventory', component: ConsignmentInventory },
    {
        path: '/consignmentReturnInventory',
        component: ConsignmentReturnInventory,
    },
    //{ path: '/priceOverride', component: EquityReportUsageContainer },   //nami
    {
        exact: true,
        path: '/ImpersonationLanding',
        component: ImpersonationLandingScreen,
    },
    { path: '/physicians', component: Physicians },
    { path: '/proxies', component: Proxies },
    { path: '/productGuide', component: ProductGuide },
    { path: '/adminView', component: Admin },
    { path: '/templates', component: Templates },
    { path: '/orderHistory', component: OrderHistory },
    { path: '/backOrders', component: BackOrders },
    { path: '/receiveSets', component: ReceiveSets },
    { path: '/error', component: ErrorComponent },
    { path: '/consignmentAdd', component: ConsignmentAdd },
    { path: '/fieldInventoryDashboard', component: FieldInventoryDashboard },
    { path: '/overrides/:linkMyCaseRef', component: Overrides },
    { path: '/overrides', component: Overrides },
    { path: '/alerts', component: Alerts },
];
